import type { InlineToolRunnable } from '../tools/types';

export interface InlineAssistantPromptOptions {
  prompt: string;
  agentName?: string;
  agentId?: string;
  systemPrompt?: string;
  tools?: InlineToolRunnable[];
  onStart?: () => void;
  onDelta?: (delta: string) => void;
  onComplete?: (text: string) => void;
  onError?: (error: Error) => void;
}

export interface InlineAssistant {
  sendPrompt(options: InlineAssistantPromptOptions): Promise<void>;
  cancel(): void;
  dispose(): void;
}

// Extend window interface for global registry
declare global {
  interface Window {
    [key: string]: any;
  }
}

export type InlineAssistantFactory = (origin: string) => Promise<InlineAssistant>;
export type InlineAssistantInitializer = () => Promise<void>;

// Global registry keys for cross-plugin sharing
const GLOBAL_FACTORY_KEY = '__grafanaAssistantInlineFactory__';
const GLOBAL_INITIALIZER_KEY = '__grafanaAssistantInlineInitializer__';
const GLOBAL_INITIALIZATION_PROMISE_KEY = '__grafanaAssistantInlineInitPromise__';

export function setInlineAssistantFactory(fn: InlineAssistantFactory): void {
  const existing = window[GLOBAL_FACTORY_KEY] as InlineAssistantFactory | undefined;
  if (existing && existing !== fn) {
    console.warn('InlineAssistant factory already set; ignoring subsequent setInlineAssistantFactory call');
    return;
  }
  if (!existing) {
    window[GLOBAL_FACTORY_KEY] = fn;
  }
}

/**
 * Register a lazy initializer that will be called on first use.
 * The plugin code should register this to enable lazy loading of the inline assistant.
 *
 * This uses global storage (window) so that all plugins share the same initializer,
 * regardless of which plugin instance or package version loads first.
 */
export function setInlineAssistantInitializer(fn: InlineAssistantInitializer): void {
  const existing = window[GLOBAL_INITIALIZER_KEY] as InlineAssistantInitializer | undefined;
  if (existing && existing !== fn) {
    console.warn('InlineAssistant initializer already set; ignoring subsequent setInlineAssistantInitializer call');
    return;
  }
  if (!existing) {
    window[GLOBAL_INITIALIZER_KEY] = fn;
  }
}

/**
 * Ensure the inline assistant is initialized. If an initializer is registered,
 * it will be called on first use. Otherwise, checks if factory is already registered.
 *
 * Uses global storage (window) so that initialization is shared across all plugins.
 */
export async function ensureInlineAssistantInitialized(): Promise<void> {
  // If factory is already set, we're good
  const factory = window[GLOBAL_FACTORY_KEY] as InlineAssistantFactory | undefined;
  if (factory) {
    return;
  }

  // If no initializer is registered, throw error
  const initializer = window[GLOBAL_INITIALIZER_KEY] as InlineAssistantInitializer | undefined;
  if (!initializer) {
    throw new Error(
      'InlineAssistant not initialized. Make sure the Grafana Assistant plugin is loaded and initialized.'
    );
  }

  // If initialization is in progress, wait for it
  const existingPromise = window[GLOBAL_INITIALIZATION_PROMISE_KEY] as Promise<void> | null | undefined;
  if (existingPromise) {
    return existingPromise;
  }

  // Start initialization
  const initPromise = (async () => {
    try {
      await initializer();
    } catch (err) {
      // Reset on error so retry is possible
      window[GLOBAL_INITIALIZATION_PROMISE_KEY] = null;
      throw err;
    }
  })();

  window[GLOBAL_INITIALIZATION_PROMISE_KEY] = initPromise;
  return initPromise;
}

export function getInlineAssistantFactory(): InlineAssistantFactory {
  const factory = window[GLOBAL_FACTORY_KEY] as InlineAssistantFactory | undefined;
  if (!factory) {
    throw new Error('InlineAssistant factory not initialized. Make sure the Grafana Assistant plugin is loaded.');
  }
  return factory;
}
